import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { userService } from "../services/api";
import { toast } from "react-hot-toast";
import "./User.css";

const User = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    role: "User",
    department: "",
    programme: "",
  });
  const [editingId, setEditingId] = useState(null);

  const roles = [
    "User",
    "Networking Staff",
    "Plumber",
    "Electrician",
    "Software Developer",
    "Application",
    "PC Hardware",
  ];

  useEffect(() => {
    if (user?.role === "SuperAdmin") {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await userService.getAll();
      setUsers(response.data);
      setError("");
    } catch (err) {
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (!editingId) {
        if (!submitData.password) {
          setError("Password is required for new users");
          return;
        }
      } else {
        if (!submitData.password) {
          delete submitData.password;
        }
      }

      // Phone validation
      if (submitData.phone && !/^\d{10}$/.test(submitData.phone)) {
        setError("Phone number must be exactly 10 digits");
        toast.error("Phone number must be exactly 10 digits");
        return;
      }

      // Password validation
      const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,15}$/;
      if (submitData.password && !passwordRegex.test(submitData.password)) {
        setError(
          "Password must be 8-15 characters with Uppercase, Lowercase, Number and Special char",
        );
        toast.error(
          "Weak password! Use 8-15 chars with A-Z, a-z, 0-9, and special char",
        );
        return;
      }

      if (editingId) {
        if (
          !window.confirm(
            "Are you sure you want to update this user's profile information?",
          )
        )
          return;
        const load = toast.loading("Updating user...");
        await userService.update(editingId, submitData);
        toast.dismiss(load);
      } else {
        const load = toast.loading("Creating user...");
        await userService.create(submitData);
        toast.dismiss(load);
      }
      setFormData({
        username: "",
        email: "",
        phone: "",
        password: "",
        role: "User",
        department: "",
        programme: "",
      });
      setEditingId(null);
      setShowForm(false);
      toast.success(
        editingId ? "User updated successfully!" : "User created successfully!",
      );
      fetchData();
    } catch (err) {
      toast.dismiss();
      const msg = err.response?.data?.message || "Failed to save user";
      setError(msg);
      toast.error(msg);
    }
  };

  const handleEdit = (userData) => {
    setFormData({
      username: userData.username,
      email: userData.email,
      phone: userData.phone,
      password: "",
      role: userData.role,
    });
    setEditingId(userData._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    ) {
      const load = toast.loading("Deleting user...");
      try {
        await userService.delete(id);
        toast.dismiss(load);
        toast.success("User deleted successfully");
        fetchData();
      } catch (err) {
        toast.dismiss(load);
        toast.error("Failed to delete user");
      }
    }
  };

  if (loading)
    return (
      <div className="user-page">
        <p>Loading...</p>
      </div>
    );

  return (
    <div className="user-page">
      <div className="user-page-header">
        <h1>User Management</h1>
        {user?.role === "SuperAdmin" && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="primary-action-btn"
          >
            {showForm ? "Cancel" : "Add User"}
          </button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {showForm && user?.role === "SuperAdmin" && (
        <form onSubmit={handleSubmit} className="user-form-card">
          <div className="form-grid-2">
            <div className="form-group-item">
              <label>Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                required
                placeholder="e.g. jdoe"
              />
            </div>
            <div className="form-group-item">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                placeholder="e.g. john@example.com"
              />
            </div>
            <div className="form-group-item">
              <label>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                minLength="10"
                maxLength="10"
                pattern="[0-9]{10}"
                title="Phone number must be exactly 10 digits"
                placeholder="e.g. 9876543210"
              />
            </div>
            <div className="form-group-item">
              <label>Role</label>
              <select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
              >
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group-item">
              <label>
                Password{" "}
                {editingId && (
                  <span style={{ fontSize: "0.8em", fontWeight: "normal" }}>
                    (leave empty to keep current)
                  </span>
                )}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required={!editingId}
                  minLength="8"
                  maxLength="25"
                  pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}"
                  title="Password must be 8-20 chars with uppercase, lowercase, number and special char"
                  placeholder={editingId ? "******" : "8-20 chars strong pw"}
                  style={{ paddingRight: "3rem" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748b",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
          <button
            type="submit"
            className="primary-action-btn"
            style={{ width: "100%", marginTop: "1.5rem" }}
          >
            {editingId ? "Update User" : "Create User"}
          </button>
        </form>
      )}

      <div className="user-table-card">
        <div className="table-wrapper">
          <table className="user-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                {user?.role === "SuperAdmin" && (
                  <th style={{ textAlign: "right" }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((userData) => (
                  <tr key={userData._id}>
                    <td>
                      <strong>{userData.username}</strong>
                    </td>
                    <td>{userData.email}</td>
                    <td>{userData.phone}</td>
                    <td>
                      <span
                        className={`role-badge ${userData.role.toLowerCase().split(" ").join("-")}`}
                      >
                        {userData.role}
                      </span>
                    </td>
                    {user?.role === "SuperAdmin" && (
                      <td style={{ textAlign: "right" }}>
                        <div className="user-actions">
                          <button
                            onClick={() => handleEdit(userData)}
                            className="edit-action-btn"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(userData._id)}
                            className="delete-action-btn"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={user?.role === "SuperAdmin" ? 5 : 4}
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#64748b",
                    }}
                  >
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default User;
